import React from 'react';
import { X, Check, Lock, AlertTriangle, Star } from 'lucide-react';
interface ExitIntentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
  basePrice?: number;
}
export function ExitIntentPopup({
  isOpen,
  onClose,
  onAccept,
  onDecline,
  basePrice = 8900
}: ExitIntentPopupProps) {
  if (!isOpen) return null;
  const bumpsTotal = 18000; // 9000 + 9000
  const fullPrice = basePrice + bumpsTotal;
  const discountedPrice = fullPrice * 0.7; // 30% OFF
  return <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-6" data-id="element-385">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={onClose} data-id="element-386" />

      {/* Popup Card — scrollable, max height constrained */}
      <div className="relative w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-387">
        {/* Header Banner — fixed, doesn't scroll */}
        <div className="bg-red-600 text-white p-3 sm:p-4 text-center relative overflow-hidden flex-shrink-0" data-id="element-388">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%,transparent_100%)] bg-[length:250%_250%,100%_100%] animate-[shimmer_3s_infinite]" data-id="element-389" />
          <div className="relative z-10 flex items-center justify-center gap-1.5 mb-1" data-id="element-390">
            <AlertTriangle className="w-4 h-4 text-yellow-300 fill-yellow-300" data-id="element-391" />
            <span className="font-bold uppercase tracking-wider text-xs text-yellow-300" data-id="element-392">
              ¡Esperá! No cierres todavía
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold font-serif leading-tight relative z-10" data-id="element-393">
            ¡PARÁ! No te vayas sin tu tratamiento completo ✋
          </h2>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6" data-id="element-394">
          {/* Discount Badge */}
          <div className="flex justify-center mb-4" data-id="element-395">
            <div className="bg-yellow-100 text-yellow-800 border border-yellow-300 px-3 py-1 rounded-full text-xs sm:text-sm font-bold flex items-center gap-1.5 animate-pulse" data-id="element-396">
              <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" data-id="element-397" />
              OFERTA ÚNICA: 30% OFF EN LOS UPGRADES
            </div>
          </div>

          {/* Copy */}
          <div className="text-center mb-4" data-id="element-398">
            <p className="text-slate-700 text-base sm:text-lg font-medium mb-1" data-id="element-399">
              Estás a un paso de tener el control total de tu ansiedad.
            </p>
            <p className="text-slate-500 text-xs sm:text-sm" data-id="element-400">
              Sumá el re-cableado neuronal y el protocolo de sueño para atacar
              la ansiedad desde todos los frentes por una fracción del precio.
            </p>
          </div>

          {/* What's Included */}
          <div className="bg-slate-50 rounded-xl p-3 sm:p-4 mb-4 border border-slate-100" data-id="element-401">
            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 text-center" data-id="element-402">
              TODO LO QUE TE LLEVÁS
            </p>
            <ul className="space-y-1.5 sm:space-y-2" data-id="element-403">
              <li className="flex items-start gap-2 text-xs sm:text-sm text-slate-700" data-id="element-404">
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-405" />
                <span className="font-medium" data-id="element-406">
                  Kit de Emergencia SOS Ansiedad
                </span>
              </li>
              <li className="flex items-start gap-2 text-xs sm:text-sm text-slate-700" data-id="element-407">
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-408" />
                <span data-id="element-409">3 Bonos Exclusivos de Regalo</span>
              </li>
              <li className="flex items-start gap-2 text-xs sm:text-sm text-slate-800 bg-yellow-50/80 p-1.5 -mx-1 rounded" data-id="element-410">
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-411" />
                <span className="font-bold" data-id="element-412">
                  Entrenamiento Re-cableado Neuronal
                </span>
              </li>
              <li className="flex items-start gap-2 text-xs sm:text-sm text-slate-800 bg-yellow-50/80 p-1.5 -mx-1 rounded" data-id="element-413">
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-414" />
                <span className="font-bold" data-id="element-415">Protocolo de Sueño Profundo</span>
              </li>
            </ul>
          </div>

          {/* Pricing */}
          <div className="text-center mb-4" data-id="element-416">
            <div className="flex items-center justify-center gap-2 mb-1" data-id="element-417">
              <span className="text-slate-400 line-through text-base" data-id="element-418">
                AR$ {fullPrice.toLocaleString('es-AR')}
              </span>
              <span className="text-2xl sm:text-3xl font-bold text-blue-600" data-id="element-419">
                AR$ {discountedPrice.toLocaleString('es-AR')}
              </span>
            </div>
            <p className="text-[10px] sm:text-xs text-red-500 font-medium" data-id="element-420">
              *Precio especial disponible solo en esta pantalla
            </p>
          </div>

          {/* CTAs */}
          <div className="space-y-2" data-id="element-421">
            <button onClick={onAccept} className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 sm:py-4 px-4 rounded-xl shadow-lg shadow-green-500/30 transition-all flex items-center justify-center gap-2 text-sm sm:text-base" data-id="element-422">
              <span data-id="element-423">SÍ, QUIERO EL PACK COMPLETO</span>
              <span className="bg-green-700/50 px-2 py-0.5 rounded text-xs sm:text-sm" data-id="element-424">
                AR$ {discountedPrice.toLocaleString('es-AR')}
              </span>
            </button>

            <div className="relative flex items-center gap-3 py-2" data-id="element-425">
              <div className="flex-1 border-t border-slate-200" data-id="element-426"></div>
              <span className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider font-medium" data-id="element-427">
                o bien
              </span>
              <div className="flex-1 border-t border-slate-200" data-id="element-428"></div>
            </div>

            <button onClick={onDecline} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 sm:py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 text-xs sm:text-sm shadow-md shadow-blue-500/20" data-id="element-429">
              <span data-id="element-430">Solo quiero el Kit SOS básico</span>
              <span className="bg-blue-700/50 px-2 py-0.5 rounded font-bold" data-id="element-431">
                AR$ {basePrice.toLocaleString('es-AR')}
              </span>
            </button>
          </div>

          {/* Trust */}
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-slate-400" data-id="element-432">
            <Lock className="w-3 h-3" data-id="element-433" />
            <span data-id="element-434">Pago 100% seguro y encriptado</span>
          </div>
        </div>

        {/* Close button */}
        <button onClick={onClose} className="absolute top-1.5 right-1.5 p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10" aria-label="Cerrar" data-id="element-435">
          <X className="w-5 h-5" data-id="element-436" />
        </button>
      </div>
    </div>;
}